import { LABCORP_MVX_PLUS_URL, MVX_MARKERS } from './MetabolicVulnerability';

/**
 * References for the MVX Plus section.
 *
 * MVX isn't a Section, so it can't use the DB-driven Reference Library (the
 * SECTION_Gn_NN tag rows every other section's References tab resolves through
 * `findByTag`). These live in a static module instead and render via
 * MvxReferencesSection — which means they are NOT editable from the sheet; a
 * change here is a code change. Same trade as `mvxAbout.ts` / `mvxSupplements.ts`.
 *
 * SHAPE follows the house References tab exactly (see `renderReferenceItem` in
 * further-considerations-section.tsx): rows are GROUPED under a title, and each
 * item prints "<citation>  —  <identifier>" with the identifier linking out.
 * Citations are AMA with at most three authors then "et al."
 * (see memory `feedback_citation_three_authors`).
 *
 * NOT numbered, deliberately. A References row earns its "(n)" bibliography
 * styling only when every item is numbered, and the numbers exist so inline
 * "(n)" markers in Causes/Symptoms text can point at them. No MVX prose carries
 * those markers, so numbering here would promise a cross-reference that isn't
 * there; bullets are the correct house styling for an unnumbered row.
 *
 * Range provenance moved OUT of this list on 2026-10-07 and into
 * MVX_RANGE_PROVENANCE, which renders as a table — see the comment there.
 *
 * COVERAGE (all six factors now have marker-specific papers; PMIDs verified
 * against PubMed 2026-07-24, 2026-10-05 and 2026-10-07):
 *
 *   Otvos 2023      the MVX validation paper itself, the one the score is
 *                   derived from and the one Schmidt cites in his NEXGEN deck
 *   GlycA           3 papers, high side
 *   Small HDL-P     2 papers, both sides
 *   Citrate         2 papers, HIGH SIDE ONLY (added 2026-10-07)
 *   BCAAs           3 papers, both sides (added 2026-10-07)
 *
 * TWO LOW SIDES REMAIN UNSUPPORTED, and they are the two the bars still draw:
 * a low citrate and a high Small HDL-P. Neither is a described clinical state
 * — searches return the urine literature (hypocitraturia and kidney stones)
 * for the first and nothing at all for the second. Both are modeled as windows
 * because Schmidt gives a band, so each bar has an edge that no paper backs.
 */
export interface MvxReference {
  /** AMA citation, at most three authors then "et al." */
  citation: string;
  /**
   * PMID, or another short stable id for a non-journal source. Becomes the
   * link text, as the house tab prints a PMID. Omit and the URL prints instead.
   */
  identifier?: string;
  /**
   * Omit for a source with nowhere to point — unpublished teaching has no DOI,
   * PMID or public page, and inventing a link to somebody's homepage would
   * dress a citation up as a reference. The row then renders as plain text.
   */
  url?: string;
}

export interface MvxReferenceGroup {
  /** Rendered as the `consideration-title` above the group, house-style. */
  title: string;
  references: MvxReference[];
}

/** One row of the range-provenance table: which band, on which markers, from where. */
export interface MvxRangeProvenance {
  band: 'Optimal' | 'Lab' | 'Alarm';
  markers: string;
  source: string;
  /** True when nobody published it and we picked it — rendered as a caveat. */
  chosen?: boolean;
  /**
   * True when the row is waiting on Dr. Schmidt — a bound he has not stated
   * exactly (IVX), or one of ours he has not confirmed. Badged separately from
   * `chosen`, because "we picked this" and "he has to settle this" are not the
   * same caveat.
   */
  ask?: boolean;
  /**
   * PMID (or DOI) of the study the `source` names, printed at the end of the
   * row and linked, in the same "<text>  —  <identifier>" form the citation
   * list uses.
   *
   * It repeats what the References tab already holds, deliberately: a reader
   * who never opens that tab has no way to tell which Otvos paper the row
   * means. The identifier travels with the claim.
   */
  identifier?: string;
  /** Where the identifier points. PubMed or doi.org only. */
  url?: string;
}

/**
 * Where every colored band on every MVX bar comes from.
 *
 * A TABLE rather than a bulleted list because the content is a matrix — band
 * against source — and as prose the rows read as nine different statements
 * when they are really one statement nine times. It also makes the `chosen`
 * column impossible to miss, which matters: several of these bounds are ours
 * and nobody's published figure, and that should be visible at a glance
 * rather than buried in a sentence.
 *
 * Keep in step with MVX_MARKERS and MVX_INDICES. A test recomputes the stated
 * arithmetic and fails if a bound drifts from the derivation printed here.
 *
 * EVERY NUMBER IN A SOURCE STRING IS A STORED BOUND, not a printed threshold.
 * Labcorp prints the three index limits as "<63", "<59" and "<71", which is not
 * the lab max — 62, 58 and 70 are, and those are the numbers in the DB rows, on
 * the bars and in the Master Ranges Sheet. A bare "63" in this table read as the
 * lab max and caused exactly that confusion (2026-10-07), so where the printed
 * form has to appear it is written out as "under 63" and the stored bound is
 * named in the same breath.
 */
/**
 * The note above the table, not a row in it.
 *
 * It was the first row for a while, under a Band of "Optimal" and a Markers
 * of "How to read these bars", which is neither a band nor a marker: it is
 * how to read the whole page (Maryann, 2026-10-15). A row that has to lie
 * about two of its three columns belongs somewhere else.
 */
export const MVX_HOW_TO_READ = {
  heading: 'How to read the bar graphs',
  text:
    'Four bars are built the same way and carry a target inside the optimal band: MVX, IVX, GlycA, and MMX. Lower is better on all four, the whole way down, so the green deepens toward the left end of the bar rather than sitting flat, and a result well short of the target is better than one that just reaches it. Dr. Schmidt treats these scales as a gradient rather than a set of cut-offs, so the green gives way to yellow gradually above the target instead of at a line, and the bands past it shade from yellow through orange into red. The two numbers do different jobs: the target is the number a result is judged against, and the end of the band is as far as the bar paints green. A GlycA of 275 sits inside the drawn band and is still called out as inflammation, because the call is made against the target.',
};

/** The heading over the table itself. */
export const MVX_PROVENANCE_HEADING = 'The source for every band';

export const MVX_RANGE_PROVENANCE: MvxRangeProvenance[] = [
  // WRITTEN FOR THE PRACTITIONER READING IT. State where the number comes
  // from, nothing else: no dates, no what-it-used-to-be, no reasoning about
  // how the bar is drawn. Our own history belongs in the code comments and
  // the git log, not on a clinical page. (Maryann, 2026-10-09.)
  //
  // Row order is bar order: MVX, IVX, MMX, then the six factors.
  {
    band: 'Optimal',
    markers: 'MVX',
    source:
      'Target 34, borderline to 39. The target is the top of the lowest group in the five-year mortality curve of Otvos 2023: in a cohort of 5,876, that group ran about 5% mortality at five years, against roughly 61% at 75 and above. Dr. Schmidt holds the green to 39, which sits inside the next group up in the same curve, 35 to 44.',
    identifier: 'PMID: 36738747',
    url: 'https://pubmed.ncbi.nlm.nih.gov/36738747/',
  },
  {
    band: 'Optimal',
    markers: 'IVX',
    source:
      'Target 25, borderline to 39. Dr. Schmidt reads IVX as good in the 20’s and 30’s and better in the 20’s, and lower is better the whole way down: there is no point at which the score stops improving.',
  },
  {
    band: 'Optimal',
    markers: 'MMX',
    source:
      'Target 45, borderline to 49. Both are Dr. Schmidt’s: 45 is the figure he works to, and 49 is the top of his “best in the high 40’s”.',
  },
  {
    band: 'Optimal',
    markers: MVX_MARKERS.map(m => m.short).join(', '),
    source:
      'Dr. Darren Schmidt’s MVX Plus metabolic resilience ranges:\nGlycA 180–300 with a target of 260, Small HDL-P 18–21.6, citrate 1.5–1.9, valine 210–250, leucine 130–170, isoleucine 45–65.\nThey come from the score itself: each range is centered on the value that contributes 1 to the MVX. Dr. Schmidt allows wiggle room in them, and points to a citrate above 2.0 with low BCAAs and an MMX in the low 40’s as a picture that still reads well.',
  },
  {
    band: 'Optimal',
    markers: 'GlycA, MMX',
    source:
      'These two bars start above zero, at 100 and 34. Dr. Schmidt has not seen a GlycA below 100 or an MMX below 36, and the MMX bar starts two points under his lowest so a new record has somewhere to plot. A result under the start of a bar is not flagged for it; it sits at the left edge.',
  },
  {
    band: 'Lab',
    markers: 'All nine',
    source:
      'Labcorp test #123601: the printed reference intervals for the six factors and the medical decision limits for the three scores, matched exactly. Four are printed as a “less than”, and the bound shown is the highest value still inside: the scores read under 63, under 59 and under 71 on EDTA plasma, so they show 62, 58 and 70, and GlycA reads under 400, so it shows 399. Same interval, written as the last value that still counts as normal.',
  },
  {
    band: 'Alarm',
    markers: 'MVX, IVX',
    source:
      'Alarm begins at 78 and 74, fifteen points past Labcorp’s limits, so the Elevated band runs 63–77 and 59–73. Dr. Schmidt reads these scores as a gradient rather than a cliff, “all these numbers are gradiently bad”, so the bar shades into red from the lab limit onward instead of stepping at a line.',
  },
  {
    band: 'Alarm',
    markers: 'MMX',
    source:
      'Alarm begins at 76, Dr. Schmidt’s own figure for this score, so the Elevated band runs 71–75.',
  },
  {
    band: 'Alarm',
    markers: 'GlycA',
    source:
      'Alarm begins at 500. Dr. Schmidt: “500 is really bad, that is the first alarm. 700 is terrible.” The bar holds 499 as the last value before the red.',
  },
  {
    band: 'Alarm',
    markers: 'Small HDL-P',
    source:
      '9.0 and 24.1, mean ± 3 SD off Labcorp’s printed 11.5–21.6 interval.',
  },
  {
    band: 'Alarm',
    markers: 'Citrate, Valine, Leucine, Isoleucine',
    source:
      'Four of these are Dr. Schmidt’s own: valine’s 400, isoleucine’s 27 and 150, and leucine’s low of 40. The rest are mean ± 3 SD off each marker’s printed interval, the same derivation Small HDL-P uses: citrate 0.67 and 3.14, valine’s low 70.5, leucine’s high 213.5. Leucine’s low takes his figure because the arithmetic cannot serve there: its printed interval is 138 wide, so three SD below the midpoint lands at 6.5, under anything a living person measures. His 40 is one below the printed 41, the rule he uses on isoleucine.',
  },
];

/*
 * There is no note under the table any more. It explained the "less than"
 * convention — that Labcorp's "<400" stores as 399 — in a paragraph that
 * repeated what the Lab row of the table now says in its own words. One place
 * states it, and it is the row the reader is already looking at when the
 * question occurs to them.
 */

export const MVX_REFERENCES: MvxReferenceGroup[] = [
  {
    title: 'Test References — MVX Plus by NMR',
    references: [
      {
        // THE MVX paper: the study the score was derived and validated in
        // (CATHGEN n=5876 + Intermountain Heart n=2888, sex-specific MVX from
        // the same six NMR biomarkers this panel reports). Funded by Labcorp.
        citation:
          'Otvos JD, Shalaurova I, May HT, et al. Multimarkers of metabolic malnutrition and inflammation and their association with mortality risk in cardiac catheterisation patients: a prospective, longitudinal, observational, cohort study. Lancet Healthy Longev. 2023;4(2):e72-e82.',
        identifier: 'PMID: 36738747',
        url: 'https://pubmed.ncbi.nlm.nih.gov/36738747/',
      },
      {
        // Labcorp's own second reference: MVX validated in a heart-failure
        // cohort (n=1,382) independent of the derivation study.
        citation:
          'Conners KM, Shearer JJ, Joo J, et al. The Metabolic Vulnerability Index: a novel marker for mortality prediction in heart failure. JACC Heart Fail. 2024;12(2):290-300.',
        identifier: 'PMID: 37480881',
        url: 'https://pubmed.ncbi.nlm.nih.gov/37480881/',
      },
      {
        citation: 'Labcorp. Metabolic Vulnerability Index (MVX) Plus by NMR.',
        // No PMID on a vendor test page; its catalog number is the stable id,
        // and it reads better than an 80-character URL in a bullet.
        identifier: 'Test #123601',
        url: LABCORP_MVX_PLUS_URL,
      },
    ],
  },
  {
    title: 'Marker References — GlycA',
    references: [
      {
        citation:
          'Akinkuolie AO, Buring JE, Ridker PM, et al. A novel protein glycan biomarker and future cardiovascular disease events. J Am Heart Assoc. 2014;3(5):e001221.',
        identifier: 'PMID: 25249300',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25249300/',
      },
      {
        citation:
          'Duprez DA, Otvos J, Sanchez OA, et al. Comparison of the predictive value of GlycA and other biomarkers of inflammation for total death, incident cardiovascular events, noncardiovascular and noncancer inflammatory-related events, and total cancer events. Clin Chem. 2016;62(7):1020-1031.',
        identifier: 'PMID: 27173011',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27173011/',
      },
      {
        // What the GlycA signal physically is, for the About/References reader
        // who asks why an NMR peak reports inflammation.
        citation:
          'Noel M, Chasman DI, Mora S, et al. The inflammation biomarker GlycA reflects plasma N-glycan branching. Clin Chem. 2023;69(1):80-87.',
        identifier: 'PMID: 36254612',
        url: 'https://pubmed.ncbi.nlm.nih.gov/36254612/',
      },
      {
        citation:
          'Ritchie SC, Würtz P, Nath AP, et al. The biomarker GlycA is associated with chronic inflammation and predicts long-term risk of severe infection. Cell Syst. 2015;1(4):293-301.',
        identifier: 'PMID: 27136058',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27136058/',
      },
      {
        citation:
          'Lawler PR, Akinkuolie AO, Chandler PD, et al. Circulating N-linked glycoprotein acetyls and longitudinal mortality risk. Circ Res. 2016;118(7):1106-1115.',
        identifier: 'PMID: 26951635',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26951635/',
      },
      {
        citation:
          'Kianoush S, Bittencourt MS, Lotufo PA, et al. Association between smoking and serum GlycA and high-sensitivity C-reactive protein levels: the Multi-Ethnic Study of Atherosclerosis (MESA) and Brazilian Longitudinal Study of Adult Health (ELSA-Brasil). J Am Heart Assoc. 2017;6(8):e006545.',
        identifier: 'PMID: 28838917',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28838917/',
      },
      {
        citation:
          'Manmadhan A, Lin BX, Zhong J, et al. Elevated GlycA in severe obesity is normalized by bariatric surgery. Diabetes Obes Metab. 2019;21(1):178-182.',
        identifier: 'PMID: 30047224',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30047224/',
      },
      {
        citation:
          'Ormseth MJ, Chung CP, Oeser AM, et al. Utility of a novel inflammatory marker, GlycA, for assessment of rheumatoid arthritis disease activity and coronary atherosclerosis. Arthritis Res Ther. 2015;17(1):117.',
        identifier: 'PMID: 25956924',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25956924/',
      },
      {
        citation:
          'Connelly MA, Gruppen EG, Wolak-Dinsmore J, et al. GlycA, a marker of acute phase glycoproteins, and the risk of incident type 2 diabetes mellitus: PREVEND study. Clin Chim Acta. 2015;452:10-17.',
        identifier: 'PMID: 26549655',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26549655/',
      },
      {
        citation:
          'Bourgonje AR, van der Vaart A, Gruppen EG, et al. Plasma levels of GlycA, a pro-inflammatory glycoprotein biomarker, associate with an increased risk of microvascular complications in patients with type 2 diabetes (Zodiac-62). Endocrine. 2023;80(2):312-316.',
        identifier: 'PMID: 36757630',
        url: 'https://pubmed.ncbi.nlm.nih.gov/36757630/',
      },
      {
        citation:
          'Choi Y, Jacobs DR, Kramer HJ, et al. Nontraditional risk factors for progression through chronic kidney disease risk categories: the Coronary Artery Risk Development in Young Adults Study. Am J Med. 2023;136(4):380-389.',
        identifier: 'PMID: 36565799',
        url: 'https://pubmed.ncbi.nlm.nih.gov/36565799/',
      },
    ],
  },
  {
    // CITRATE, sourced 2026-10-07 after three earlier sweeps found nothing.
    // The HIGH side is well covered and by the right people: Fischer is NMR
    // profiling in 17,345 people where citrate is one of only four biomarkers
    // that predicted all-cause mortality, and Li is NMR plasma citrate as an
    // explicit proxy for mitochondrial dysfunction — co-authored by Labcorp's
    // own NMR group (M.A. Connelly), the same lab that runs MVX Plus.
    //
    // THE LOW SIDE STILL HAS NOTHING. Searches for hypocitratemia, low plasma
    // citrate and TCA substrate depletion return the urine literature
    // (hypocitraturia and kidney stones) or noise. Low plasma citrate is not a
    // described clinical state, so the window's low edge is unsupported.
    title: 'Marker References — Citrate',
    references: [
      {
        citation:
          'Fischer K, Kettunen J, Würtz P, et al. Biomarker profiling by nuclear magnetic resonance spectroscopy for the prediction of all-cause mortality: an observational study of 17,345 persons. PLoS Med. 2014;11(2):e1001606.',
        identifier: 'PMID: 24586121',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24586121/',
      },
      {
        citation:
          'Li Y, Chvatal-Medina M, Trillos-Almanza MC, et al. Circulating citrate is reversibly elevated in patients with end-stage liver disease: association with all-cause mortality. Int J Mol Sci. 2024;25(23):12806.',
        identifier: 'PMID: 39684514',
        url: 'https://pubmed.ncbi.nlm.nih.gov/39684514/',
      },
      {
        citation:
          'Fuster DG, Moe OW. Incomplete distal renal tubular acidosis and kidney stones. Adv Chronic Kidney Dis. 2018;25(4):366-374.',
        identifier: 'PMID: 30139463',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30139463/',
      },
    ],
  },
  {
    // These two are why Small HDL-P is modeled as a window rather than a
    // floor (see its entry in MVX_MARKERS). Cited because the band on that bar
    // departs from Schmidt's stated ceiling, so the reason should be readable.
    title: 'Marker References — Small HDL-P',
    references: [
      {
        citation:
          'Harsløf M, Pedersen KM, Afzal S, et al. Lower levels of small HDL particles associated with increased infectious disease morbidity and mortality: a population-based cohort study of 30 195 individuals. Cardiovasc Res. 2023;119(4):957-968.',
        identifier: 'PMID: 36537045',
        url: 'https://pubmed.ncbi.nlm.nih.gov/36537045/',
      },
      {
        citation:
          'Gourgari E, Playford MP, Campia U, et al. Low cholesterol efflux capacity and abnormal lipoprotein particles in youth with type 1 diabetes: a case control study. Cardiovasc Diabetol. 2018;17(1):158.',
        identifier: 'PMID: 30567548',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30567548/',
      },
      {
        citation:
          'Beauchamp A, Tonkin A, Peeters A, et al. Associations among smoking status, lifestyle and lipoprotein subclasses. J Clin Lipidol. 2010;4(6):522-530.',
        identifier: 'PMID: 21122700',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21122700/',
      },
    ],
  },
  {
    // The three BCAAs share one group: they are one signal, and the bullet
    // rules already fire on "any BCAA" rather than on a named one.
    //
    // BOTH DIRECTIONS are covered, which matters because the two bullets say
    // opposite things. Newgard is the landmark HIGH-side paper (a BCAA
    // signature of increased catabolism, correlated with insulin resistance in
    // obese versus lean humans). Lu is the LOW side: in 189 community-dwelling
    // older adults, branched-chain amino acids were inversely associated with
    // sarcopenia, alongside low mini-nutritional-assessment scores. Buondonno
    // is the treatment end of the same story, a randomized trial in 155
    // malnourished elderly where a BCAA-enriched mixture raised mitochondrial
    // ATP production and physical performance.
    title: 'Marker References — Valine, Leucine and Isoleucine',
    references: [
      {
        citation:
          'Newgard CB, An J, Bain JR, et al. A branched-chain amino acid-related metabolic signature that differentiates obese and lean humans and contributes to insulin resistance. Cell Metab. 2009;9(4):311-326.',
        identifier: 'PMID: 19356713',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19356713/',
      },
      {
        citation:
          'Lu Y, Karagounis LG, Ng TP, et al. Systemic and metabolic signature of sarcopenia in community-dwelling older adults. J Gerontol A Biol Sci Med Sci. 2020;75(2):309-317.',
        identifier: 'PMID: 30624690',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30624690/',
      },
      {
        citation:
          'Buondonno I, Sassi F, Carignano G, et al. From mitochondria to healthy aging: the role of branched-chain amino acids treatment: MATeR a randomized study. Clin Nutr. 2020;39(7):2080-2091.',
        identifier: 'PMID: 31672329',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31672329/',
      },
      {
        citation:
          'Holeček M. Branched-chain amino acids in health and disease: metabolism, alterations in blood plasma, and as supplements. Nutr Metab (Lond). 2018;15:33.',
        identifier: 'PMID: 29755574',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29755574/',
      },
      {
        citation:
          'Holeček M. Branched-chain amino acids and ammonia metabolism in liver disease: therapeutic implications. Nutrition. 2013;29(10):1186-1191.',
        identifier: 'PMID: 23756281',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23756281/',
      },
      {
        citation:
          'Strauss KA, Carson VJ, Soltys K, et al. Branched-chain α-ketoacid dehydrogenase deficiency (maple syrup urine disease): treatment, biomarkers, and outcomes. Mol Genet Metab. 2020;129(3):193-206.',
        identifier: 'PMID: 31980395',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31980395/',
      },
    ],
  },
];
